import React, { createContext, useContext, useState, useCallback } from 'react';
import { FiCheckCircle, FiAlertCircle, FiX } from 'react-icons/fi';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning';
}

interface NotificationContextType {
  showNotification: (message: string, type: 'success' | 'error' | 'warning') => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showNotification = useCallback((message: string, type: 'success' | 'error' | 'warning') => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);

    setTimeout(() => {
      const element = document.getElementById(`toast-${id}`);
      if (element) {
        element.classList.add('hide');
        setTimeout(() => removeToast(id), 500);
      } else {
        removeToast(id);
      }
    }, 4000);
  }, [removeToast]);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      <div className="toast-container-custom">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            id={`toast-${toast.id}`}
            className={`toast-custom ${toast.type}`}
          >
            <div className={`p-2 rounded-3 ${
              toast.type === 'success' ? 'bg-success bg-opacity-20 text-success' : 
              toast.type === 'error' ? 'bg-danger bg-opacity-20 text-danger' : 
              'bg-warning bg-opacity-20 text-warning'
            }`}>
              {toast.type === 'success' ? <FiCheckCircle size={20} /> : <FiAlertCircle size={20} />}
            </div>
            <div className="flex-grow-1 fw-bold small text-white">{toast.message}</div>
            <button
              onClick={() => {
                const element = document.getElementById(`toast-${toast.id}`);
                if (element) {
                   element.classList.add('hide');
                   setTimeout(() => removeToast(toast.id), 500);
                }
              }}
              className="btn border-0 p-1 text-white opacity-50 hover-opacity-100"
            >
              <FiX size={16} />
            </button>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
